/**
 * ChatbotFAB
 *
 * Draggable floating action button. Shows the agent icon
 * (Rocket for Pro, Compass for Guide) and toggles the chatbot panel.
 * Can be dragged anywhere on screen so it doesn't block content.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.5
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { Rocket, Compass, Bot } from 'lucide-react';

const FAB_STORAGE_KEY = 'imago_fab_position';

/** Read saved position from localStorage */
const getSavedPosition = () => {
  try {
    const raw = localStorage.getItem(FAB_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
};

/**
 * @param {Object} props
 * @param {string} props.agentType - 'pro' | 'guide' | null
 * @param {boolean} props.isOpen - whether the panel is currently open
 * @param {Function} props.onClick - toggle handler
 */
export function ChatbotFAB({ agentType, isOpen, onClick }) {
  const Icon = agentType === 'pro' ? Rocket : agentType === 'guide' ? Compass : Bot;

  const saved = getSavedPosition();
  const [position, setPosition] = useState({
    x: saved?.x ?? window.innerWidth - 80,
    y: saved?.y ?? window.innerHeight - 80,
  });
  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const dragStart = useRef({ x: 0, y: 0, startX: 0, startY: 0 });

  const handleMouseDown = useCallback((e) => {
    isDragging.current = true;
    hasMoved.current = false;
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      startX: position.x,
      startY: position.y,
    };
    e.preventDefault();
  }, [position]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) hasMoved.current = true;
      const newX = Math.max(0, Math.min(window.innerWidth - 56, dragStart.current.startX + dx));
      const newY = Math.max(0, Math.min(window.innerHeight - 56, dragStart.current.startY + dy));
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        // Save position
        try { localStorage.setItem(FAB_STORAGE_KEY, JSON.stringify(position)); } catch { /* ignore */ }
      }
    };

    // Clamp position back into viewport when window resizes (zoom change, window resize)
    const handleResize = () => {
      setPosition((prev) => {
        const clampedX = Math.max(0, Math.min(window.innerWidth - 56, prev.x));
        const clampedY = Math.max(0, Math.min(window.innerHeight - 56, prev.y));
        if (clampedX === prev.x && clampedY === prev.y) return prev;
        const clamped = { x: clampedX, y: clampedY };
        try { localStorage.setItem(FAB_STORAGE_KEY, JSON.stringify(clamped)); } catch { /* ignore */ }
        return clamped;
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('resize', handleResize);
    };
  }, [position]);

  const handleClick = useCallback(() => {
    // Only trigger click if user didn't drag
    if (!hasMoved.current) onClick?.();
  }, [onClick]);

  return (
    <button
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      aria-label={isOpen ? 'Close chatbot' : 'Open chatbot'}
      title={isOpen ? 'Close chatbot (Ctrl+J)' : 'Open chatbot (Ctrl+J)'}
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
      className={`
        fixed z-[9990]
        w-14 h-14 rounded-full
        flex items-center justify-center
        shadow-lg transition-shadow duration-200 cursor-grab active:cursor-grabbing
        select-none
        ${isOpen
          ? 'bg-primary text-primary-foreground scale-90 ring-2 ring-primary/40'
          : 'bg-primary text-primary-foreground hover:shadow-xl'
        }
      `}
    >
      <Icon className="w-6 h-6 pointer-events-none" />
    </button>
  );
}

export default ChatbotFAB;
